import React, { useState, useEffect } from 'react';

interface TimestampProps {
  timestamp: Date | string | number;
  className?: string;
  short?: boolean;
}

export const Timestamp: React.FC<TimestampProps> = ({ timestamp, short, className = '' }) => {
  const [displayText, setDisplayText] = useState('');
  const [updateInterval, setUpdateInterval] = useState(1000);

  useEffect(() => {
    const date = new Date(timestamp);
    
    const updateDisplay = () => {
      const now = new Date();
      
      // Helper function to check if two dates are the same day
      const isSameDay = (d1: Date, d2: Date) => 
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate();
      
      // Helper function to get yesterday's date
      const getYesterday = () => {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return yesterday;
      };
      
      // Helper function to get days difference
      const getDaysDiff = (d1: Date, d2: Date) => {
        const diffTime = d1.getTime() - d2.getTime();
        return Math.floor(diffTime / (1000 * 60 * 60 * 24));
      };
      
      // Format time as HH:MM
      const formatTime = (d: Date) => {
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
      };
      
      // Format date with month name
      const formatDateWithMonth = (d: Date, includeYear = false) => {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const day = d.getDate();
        const month = monthNames[d.getMonth()];
        const time = formatTime(d);
        
        if (includeYear) {
          if (short){
            return `${day} ${month} ${d.getFullYear()}`;
          }
          return `${day} ${month} ${d.getFullYear()} at ${time}`;
        }
        return `${day} ${month} at ${time}`;
      };
      
      const isToday = isSameDay(now, date);
      const isYesterday = isSameDay(getYesterday(), date);
      const daysDiff = getDaysDiff(now, date);
      const isCurrentYear = now.getFullYear() === date.getFullYear();
      
      let text = '';
      let interval = 60000; // Default to 1 minute for most cases
      
      if (isToday) {
        // Today: "Today at 16:22"
        text = `Today at ${formatTime(date)}`;
        interval = 60000; // Update every minute
      } else if (isYesterday) {
        // Yesterday: "Yesterday at 16:22"
        text = `Yesterday at ${formatTime(date)}`;
        interval = 60000; // Update every minute
      } else if (daysDiff <= 7 && daysDiff > 0 && isCurrentYear) {
        // Within a week: show day of week
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayName = dayNames[date.getDay()];
        text = `${dayName} at ${formatTime(date)}`;
        interval = 60000; // Update every minute
      } else if (isCurrentYear) {
        // Same year: "12 May at 13:22"
        text = formatDateWithMonth(date, false);
        interval = 3600000; // Update every hour for older dates
      } else {
        // Different year: "13 May 2024 at 12:44"
        text = formatDateWithMonth(date, true);
        interval = 3600000; // Update every hour for older dates
      }
      
      setDisplayText(text);
      setUpdateInterval(interval);
    };

    // Initial update
    updateDisplay();

    // Set up interval
    const intervalId = setInterval(updateDisplay, updateInterval);

    return () => clearInterval(intervalId);
  }, [timestamp, updateInterval]);

  const getFullTimestamp = () => {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  return (
    <span
      className={`cursor-help ${className}`}
      title={getFullTimestamp()}
    >
      {displayText}
    </span>
  );
};